import React from 'react';
import { useWizard } from '../../context/WizardContext';
import NavigationButtons from '../shared/NavigationButtons';
import { ClipboardList, RefreshCw, Info } from 'lucide-react';

const DATA_COLLECTION_METHODS = [
  { id: 'questionnaire', label: 'Questionnaire / Survey' },
  { id: 'interview', label: 'Interviews' },
  { id: 'focus_group', label: 'Focus groups' },
  { id: 'observation', label: 'Observation' },
  { id: 'experiment', label: 'Experiment / Intervention' },
  { id: 'secondary_data', label: 'Secondary data analysis' },
  { id: 'physiological', label: 'Physiological measures' },
  { id: 'audio_recording', label: 'Audio recording' },
  { id: 'video_recording', label: 'Video recording' },
  { id: 'photography', label: 'Photography' },
  { id: 'diary', label: 'Diary / Journal' },
  { id: 'online_platform', label: 'Online platform data' },
  { id: 'archival', label: 'Archival / Document analysis' },
  { id: 'biological_samples', label: 'Biological samples' },
  { id: 'other', label: 'Other' }
];

function D6_DataCollection() {
  const { dataE, dataA, updateDataRaw, nextStep, previousStep } = useWizard();
  const dataCollection = dataE.dataCollection;

  const updateDataCollection = (field, value) => {
    updateDataRaw({
      'wizardE.dataCollection': { ...dataCollection, [field]: value }
    });
  };

  const toggleMethod = (methodId) => {
    const currentMethods = dataCollection.methods || [];
    const newMethods = currentMethods.includes(methodId)
      ? currentMethods.filter(m => m !== methodId)
      : [...currentMethods, methodId];
    updateDataCollection('methods', newMethods);
  };

  const importFromWizardA = () => {
    const wizardATechniques = dataA.techniques?.dataCollection || [];
    const methodMapping = {
      'structuredSurvey': 'questionnaire',
      'semiStructuredInt': 'interview',
      'structuredInt': 'interview',
      'focusGroups': 'focus_group',
      'structuredObs': 'observation',
      'participantObs': 'observation',
      'experiments': 'experiment',
      'documents': 'archival',
      'secondaryData': 'secondary_data',
      'fieldNotes': 'diary',
      'visualMethods': 'photography',
      'tests': 'questionnaire'
    };
    
    const mappedMethods = wizardATechniques
      .map(t => methodMapping[t])
      .filter(Boolean);
    
    const currentMethods = dataCollection.methods || [];
    const uniqueMethods = [...new Set([...currentMethods, ...mappedMethods])];
    updateDataCollection('methods', uniqueMethods);
  };

  const hasWizardATechniques = dataA.techniques?.dataCollection?.length > 0;

  const isComplete = dataCollection.methods.length > 0 && dataCollection.instruments;

  return (
    <div className="wizard-step">
      <div className="step-header">
        <ClipboardList className="step-icon" size={32} />
        <div>
          <h2>Step 6: Data Collection</h2>
          <p className="step-description">
            Describe what data you will collect and how you will collect it.
          </p>
        </div>
      </div>

      {hasWizardATechniques && (
        <div className="info-box success">
          <RefreshCw size={20} />
          <div>
            <strong>Import from Methodology Wizard</strong>
            <p>
              Data collection methods were specified in Wizard A. You can import them here.
            </p>
            <button className="btn-secondary btn-sm" onClick={importFromWizardA}>
              Import Data Collection Methods
            </button>
          </div>
        </div>
      )}

      <div className="form-section">
        <h3>Data Collection Methods</h3>

        <div className="form-group">
          <label>What methods will you use to collect data? *</label>
          <div className="checkbox-grid">
            {DATA_COLLECTION_METHODS.map(method => (
              <label key={method.id} className="checkbox-option">
                <input
                  type="checkbox"
                  checked={dataCollection.methods?.includes(method.id) || false}
                  onChange={() => toggleMethod(method.id)}
                />
                <span>{method.label}</span>
              </label>
            ))}
          </div>
        </div>

        {dataCollection.methods?.includes('other') && (
          <div className="form-group">
            <label>Please specify other data collection method:</label>
            <input
              type="text"
              value={dataCollection.otherMethod}
              onChange={(e) => updateDataCollection('otherMethod', e.target.value)}
              placeholder="Describe the other data collection method"
            />
          </div>
        )}
      </div>

      <div className="form-section">
        <h3>Instruments and Procedures</h3>

        <div className="form-group">
          <label>Research Instruments *</label>
          <textarea
            value={dataCollection.instruments}
            onChange={(e) => updateDataCollection('instruments', e.target.value)}
            placeholder="Describe the instruments you will use (e.g., questionnaire names, interview guides, observation protocols). Include information about whether these are validated instruments or newly developed."
            rows={5}
          />
        </div>

        <div className="form-group">
          <label>Pilot Testing</label>
          <textarea
            value={dataCollection.pilotTesting}
            onChange={(e) => updateDataCollection('pilotTesting', e.target.value)}
            placeholder="Describe any pilot testing of your instruments or procedures (if applicable)"
            rows={3}
          />
        </div>

        {(dataCollection.methods?.includes('experiment') || dataCollection.methods?.includes('physiological')) && (
          <div className="form-group">
            <label>Intervention / Procedure Details</label>
            <textarea
              value={dataCollection.interventionDetails}
              onChange={(e) => updateDataCollection('interventionDetails', e.target.value)}
              placeholder="Provide detailed information about any intervention, experiment, or physiological procedure, including duration, frequency, and any potential discomfort."
              rows={5}
            />
            <div className="info-box warning" style={{ marginTop: '0.5rem' }}>
              <Info size={16} />
              <span>
                Experimental procedures may require additional ethical scrutiny. Ensure you describe any potential discomfort or risks.
              </span>
            </div>
          </div>
        )}
      </div>

      <NavigationButtons
        onPrevious={previousStep}
        onNext={isComplete ? nextStep : null}
        disableNext={!isComplete}
        nextLabel="Continue to Data Management"
      />
    </div>
  );
}

export default D6_DataCollection;
